/* Public visitor page — /d/:qrToken
   No auth required. Visitor scans QR sticker → lands here → their camera
   & mic auto-start → the resident's Guard app rings → conversation, or
   voicemail if the resident doesn't pick up.

   The heavy call/voicemail lifecycle (Agora session, MediaRecorder,
   timers, stage machine) lives in `hooks/useVisitorCall.js`. This file
   is just the router/view: resident-info fetch, error surfacing,
   auto-ring gate, marketing prompt, and the two render layouts
   (fullscreen while the camera is live, compact card otherwise). */
import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Bell, Loader2, PhoneOff, Camera, AlertCircle, ShieldCheck, Mic, Square, CheckCircle2, ExternalLink, Sparkles, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { useVisitorCall, NO_ANSWER_S, VOICEMAIL_MAX_S } from "../hooks/useVisitorCall";
import NetworkBars from "../components/doorbell/NetworkBars";
import { useCallChat } from "../hooks/useCallChat";
import CallChatPanel from "../components/doorbell/CallChatPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Post-call marketing prompt — invites the visitor to sign up for their
// own Guard doorbell once the call has wrapped up. Opens in a new tab to
// avoid disrupting a potential "call again" flow. UTM-tagged so signups
// from this viral loop are attributable in the marketing site's analytics.
const GUARD_MARKETING_URL =
  "https://www.contritrack.com/?utm_source=scan2call&utm_medium=visitor_end&utm_campaign=organic_referral";

export default function DoorbellVisitor() {
  const { qrToken } = useParams();
  const [residentInfo, setResidentInfo] = useState(null);
  const [residentInfoError, setResidentInfoError] = useState(null);

  // Ensures the auto-ring effect runs exactly once per page load — even
  // across StrictMode double-invocation in dev.
  const autoRingFiredRef = useRef(false);
  // Ensures the post-call "Get your own Guard" marketing prompt fires
  // exactly once per page load. Reset when the visitor taps "Call again"
  // so it can re-fire on a subsequent call end.
  const marketingFiredRef = useRef(false);

  const {
    stage, ring, callError,
    secsRinging, secsConnected, vmSecs,
    residentVideoOn,
    netQuality,
    previewRef, attachResidentContainer,
    handleRing, handleHangup, stopRecording, cancelVoicemail, reset,
  } = useVisitorCall({ qrToken });

  // In-call text chat — only live while connected. Messages persist on
  // the ring record so the resident can re-read them in ring history.
  // Spotlight swap — tap the resident PIP to make their video fullscreen
  // (your own preview shrinks to the PIP); tap your PIP to swap back.
  const [spotlightResident, setSpotlightResident] = useState(false);
  useEffect(() => {
    if (stage !== "connected" || !residentVideoOn) setSpotlightResident(false);
  }, [stage, residentVideoOn]);

  const chat = useCallChat({
    ringId: ring?.ring_id || null,
    mySender: "visitor",
    active: stage === "connected",
  });

  // Combined error signal — either the resident-info fetch failed (bad
  // QR token / revoked doorbell) or the call itself errored (mic denied,
  // Agora down, etc.). Rendered in a single red banner.
  const error = residentInfoError || callError;

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/doorbell/qr/public/${qrToken}`);
        setResidentInfo(r.data);
      } catch (e) {
        setResidentInfoError(e?.response?.data?.detail || "This Scan2Call link is not valid.");
      }
    })();
  }, [qrToken]);

  // Auto-fire the call the instant the resident's info loads and they're
  // active. Implements the "scan-to-open-instantly, no option after
  // scanning" UX — the visitor just lands on the page and the call
  // starts. The user gesture from tapping the QR-open prompt in their
  // phone camera is what allows getUserMedia to fire without a second
  // tap.
  //
  // Guarded by a ref so React StrictMode (which double-invokes effects
  // in dev) and any subsequent residentInfo refresh can't trigger a
  // second ring.
  useEffect(() => {
    if (autoRingFiredRef.current) return;
    if (!residentInfo) return;
    if (!residentInfo.active) return;     // visitor sees the "offline" state
    if (stage !== "idle") return;
    if (error) return;
    autoRingFiredRef.current = true;
    // Microtask delay — lets the loading UI paint at least one frame
    // before we transition to "ringing", avoiding a jarring flash.
    Promise.resolve().then(() => handleRing());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [residentInfo, stage, error]);

  // Post-call marketing prompt — once the visitor's call has fully ended
  // (either they hung up, the resident hung up, or a voicemail was left),
  // show a toast inviting them to sign up for their own Guard doorbell.
  // We DO NOT auto-open the marketing site — that felt like a hijack on
  // mobile and iOS/Android popup blockers usually eat it anyway. The
  // toast's "Sign up" action + the "Get your own Guard" CTA on the
  // ended-screen give the visitor two clear, opt-in paths instead.
  useEffect(() => {
    if (marketingFiredRef.current) return;
    if (stage !== "ended" && stage !== "voicemail_sent") return;
    marketingFiredRef.current = true;
    toast("Get your own Guard", {
      description: "Turn your own front door into a Scan2Call.",
      duration: 8000,
      action: {
        label: "Sign up",
        onClick: () => window.open(GUARD_MARKETING_URL, "_blank", "noopener,noreferrer"),
      },
    });
  }, [stage]);

  // "Call again" — reset hook state + local one-shot refs so the auto-
  // ring effect can re-fire when the visitor is back to `idle`.
  const callAgain = () => {
    autoRingFiredRef.current = false;
    marketingFiredRef.current = false;
    reset();
  };

  // ---- render ---------------------------------------------------------

  // WhatsApp-style full-viewport layout while the visitor's camera is
  // live (ringing or connected). Everything else — idle, voicemail
  // prompt/recording, ended — keeps the compact centred card because
  // there's no self-preview big enough to justify eating the whole
  // screen. `100dvh` (dynamic viewport height) beats `100vh` on mobile
  // where the URL bar can shrink the visible area mid-call.
  const isCameraStage = stage === "ringing" || stage === "connected";
  if (isCameraStage) {
    return (
      <div
        className="fixed inset-0 z-50 bg-black text-white flex flex-col"
        style={{ height: "100dvh" }}
        data-testid="doorbell-visitor-page"
      >
        {/* Video area — visitor + resident containers swap between
            fullscreen spotlight and PIP when the visitor taps the PIP
            (WhatsApp-style). Overlays live on the shared stage div. */}
        <div className="relative flex-1 w-full bg-black overflow-hidden">
          {/* Visitor's own camera */}
          <div
            ref={previewRef}
            onClick={() => { if (spotlightResident) setSpotlightResident(false); }}
            className={
              spotlightResident
                ? "absolute top-12 right-3 z-10 w-24 h-36 sm:w-28 sm:h-40 rounded-lg overflow-hidden border border-white/30 shadow-xl bg-gray-900 cursor-pointer [&>video]:w-full [&>video]:h-full [&>video]:object-cover"
                : "absolute inset-0 [&>video]:w-full [&>video]:h-full [&>video]:object-cover"
            }
            data-testid="visitor-self-preview"
          />
          {spotlightResident && (
            <span className="absolute top-[3.25rem] right-4 z-20 px-1.5 py-0.5 rounded bg-black/60 text-[9px] font-body text-gray-200 pointer-events-none">
              You
            </span>
          )}

          {/* Resident video — PIP by default; tap to spotlight. */}
          <div
            ref={attachResidentContainer}
            onClick={() => { if (!spotlightResident) setSpotlightResident(true); }}
            className={`${
              spotlightResident
                ? "absolute inset-0"
                : "absolute top-12 right-3 z-10 w-24 h-36 sm:w-28 sm:h-40 rounded-lg overflow-hidden border border-emerald-400/40 shadow-xl bg-gray-900 cursor-pointer"
            } [&>video]:w-full [&>video]:h-full [&>video]:object-cover ${
              stage === "connected" && residentVideoOn ? "" : "hidden"
            }`}
            data-testid="visitor-resident-pip"
          />

          {stage === "ringing" && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/50">
              <div className="text-center">
                <Loader2 className="w-10 h-10 mx-auto animate-spin text-emerald-400 mb-3" />
                <p className="text-lg text-white font-body font-bold" data-testid="visitor-ringing-text">
                  Ringing... {secsRinging}s
                </p>
                <p className="text-xs text-gray-300 font-body mt-1">
                  Waiting for {residentInfo?.display_name}
                </p>
                {secsRinging >= NO_ANSWER_S - 15 && (
                  <p className="text-xs text-amber-300 font-body mt-2">
                    Voicemail in {Math.max(0, NO_ANSWER_S - secsRinging)}s
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Top bar — brand pill (left) + quality bars & call timer
              (right). z-10 keeps it above the Agora-injected player. */}
          <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between p-3 pointer-events-none">
            <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-black/60 backdrop-blur-sm">
              <Bell className="w-3 h-3 text-emerald-400" />
              <span className="text-[10px] uppercase tracking-widest font-bold text-emerald-300 font-body">
                Guard · Scan2Call
              </span>
            </div>
            {stage === "connected" && (
              <div className="flex items-center gap-1.5">
                <NetworkBars quality={netQuality} testId="visitor-net-quality" />
                <div
                  className="px-2 py-1 rounded-full bg-emerald-500 text-black text-xs font-bold font-body"
                  data-testid="visitor-call-timer"
                >
                  {Math.floor(secsConnected / 60)}:{String(secsConnected % 60).padStart(2, "0")}
                </div>
              </div>
            )}
          </div>

          {/* Resident video PIP handled above (swappable spotlight). */}

          {/* Voice-only pill — only while the resident's video is off. */}
          {stage === "connected" && !residentVideoOn && (
            <div
              className="absolute top-12 left-1/2 -translate-x-1/2 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/60 backdrop-blur-sm border border-white/15 whitespace-nowrap"
              data-testid="visitor-audio-call-pill"
            >
              <span className="relative flex h-2 w-2 shrink-0">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
              </span>
              <span className="text-[11px] text-gray-100 font-body">
                {residentInfo?.display_name || "The resident"} is on voice only
              </span>
            </div>
          )}

          {/* Self "You" badge — only while your own video is the spotlight */}
          {!spotlightResident && (
            <div className="absolute bottom-3 left-3 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-black/60 backdrop-blur-sm text-[11px] font-body">
              <Camera className="w-3 h-3" /> You
            </div>
          )}
        </div>

        {/* Control bar — the visitor's camera AND mic are both mandatory
            security controls, so no Video/Mic toggles here. Affordances:
            Chat (while connected) + Hang up. */}
        <div
          className="w-full flex items-center justify-center gap-3 bg-black/95 border-t border-white/10 px-4 pt-4"
          style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 1rem)" }}
        >
          {stage === "connected" && (
            <button
              type="button"
              onClick={() => chat.setOpen(true)}
              className="relative w-12 h-12 rounded-full flex items-center justify-center transition-colors border bg-white/10 border-white/20 text-white hover:bg-white/20"
              title="Chat with resident"
              aria-label="Open chat"
              data-testid="visitor-chat-btn"
            >
              <MessageSquare className="w-5 h-5" />
              {chat.unread > 0 && (
                <span
                  className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-emerald-500 text-black text-[10px] font-bold font-body flex items-center justify-center"
                  data-testid="visitor-chat-unread"
                >
                  {chat.unread}
                </span>
              )}
            </button>
          )}
          <Button
            onClick={() => handleHangup()}
            className="w-16 h-16 rounded-full bg-red-500 hover:bg-red-600 text-white p-0 shadow-lg"
            data-testid="visitor-hangup-btn"
          >
            <PhoneOff className="w-6 h-6" />
          </Button>
        </div>

        <CallChatPanel
          open={chat.open}
          onClose={() => chat.setOpen(false)}
          messages={chat.messages}
          mySender="visitor"
          onSend={chat.sendMessage}
          sending={chat.sending}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center px-4 py-8" data-testid="doorbell-visitor-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-500/20 border-2 border-emerald-500 mb-2">
            <Bell className="w-6 h-6 text-emerald-400" />
          </div>
          <p className="text-[10px] uppercase tracking-widest font-bold text-emerald-400 font-body">Guard · Scan2Call</p>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-sm p-3 mb-4 flex items-start gap-2" data-testid="visitor-error">
            <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
            <p className="text-sm text-red-300 font-body">{error}</p>
          </div>
        )}

        {!residentInfo && !error && (
          <p className="text-center text-gray-400 font-body">Loading...</p>
        )}

        {residentInfo && stage === "idle" && (
          <div className="bg-gray-900 border border-gray-800 rounded-sm p-5" data-testid="visitor-idle">
            <p className="font-heading text-2xl font-black tracking-tighter mb-1" data-testid="visitor-resident-name">
              {residentInfo.active ? `Calling ${residentInfo.display_name}…` : residentInfo.display_name}
            </p>
            {residentInfo.active ? (
              <div className="flex items-center gap-2 mt-3 mb-2">
                <Loader2 className="w-4 h-4 animate-spin text-emerald-400 shrink-0" />
                <p className="text-xs text-gray-300 font-body">Allow camera &amp; microphone when prompted — the call connects automatically.</p>
              </div>
            ) : (
              <>
                <p className="text-xs text-gray-400 font-body mb-4">
                  This Scan2Call is currently offline.
                </p>
                <Button disabled className="w-full bg-gray-700 text-gray-400 rounded-sm font-body" data-testid="visitor-offline-btn">
                  <ShieldCheck className="w-4 h-4 mr-1.5" /> Scan2Call offline
                </Button>
              </>
            )}
          </div>
        )}

        {stage === "voicemail_prompt" && (
          <div className="bg-gray-900 border border-amber-500/40 rounded-sm p-5 text-center" data-testid="visitor-voicemail-prompt">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-amber-500/15 border border-amber-500/50 mb-3">
              <Mic className="w-6 h-6 text-amber-400" />
            </div>
            <p className="font-heading text-2xl font-black tracking-tighter mb-1">Resident unavailable</p>
            <p className="text-sm text-gray-300 font-body mb-3">Listen for the beep — recording starts automatically.</p>
            <p className="text-[10px] text-gray-500 font-body uppercase tracking-widest">Preparing voicemail…</p>
            <Button onClick={cancelVoicemail} variant="outline" size="sm" className="mt-3 rounded-sm font-body text-gray-300 border-gray-700" data-testid="visitor-voicemail-cancel">
              Cancel
            </Button>
          </div>
        )}

        {stage === "voicemail_recording" && (
          <div className="bg-gray-900 border border-red-500/40 rounded-sm p-5 text-center" data-testid="visitor-voicemail-recording">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-red-500/20 border border-red-500 mb-3 animate-pulse">
              <Mic className="w-6 h-6 text-red-400" />
            </div>
            <p className="font-heading text-2xl font-black tracking-tighter mb-1">Recording…</p>
            <p className="text-3xl font-heading font-black tracking-tighter text-red-400 mb-3" data-testid="visitor-voicemail-timer">
              {Math.floor(vmSecs / 60)}:{String(vmSecs % 60).padStart(2, "0")}
              <span className="text-sm text-gray-500"> / 0:{String(VOICEMAIL_MAX_S).padStart(2, "0")}</span>
            </p>
            <Button onClick={stopRecording} className="w-full bg-red-500 hover:bg-red-600 text-white rounded-sm font-body font-bold" data-testid="visitor-voicemail-stop">
              <Square className="w-4 h-4 mr-1.5" fill="currentColor" /> Stop &amp; Send
            </Button>
            <p className="text-[10px] text-gray-500 font-body mt-2">Auto-stops at 0:{String(VOICEMAIL_MAX_S).padStart(2, "0")}.</p>
          </div>
        )}

        {stage === "voicemail_sending" && (
          <div className="bg-gray-900 border border-gray-800 rounded-sm p-5 text-center" data-testid="visitor-voicemail-sending">
            <Loader2 className="w-8 h-8 mx-auto animate-spin text-emerald-400 mb-2" />
            <p className="text-sm text-white font-body">Sending voicemail…</p>
          </div>
        )}

        {stage === "voicemail_sent" && (
          <div className="bg-gray-900 border border-emerald-500/40 rounded-sm p-5 text-center" data-testid="visitor-voicemail-sent">
            <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto mb-2" />
            <p className="font-heading text-xl font-black tracking-tighter mb-1">Voicemail sent</p>
            <p className="text-xs text-gray-400 font-body mb-4">{residentInfo?.display_name} will hear it the next time they check Guard.</p>
            <div className="flex flex-col gap-2">
              <Button onClick={callAgain} variant="outline" className="rounded-sm font-body" data-testid="vm-call-again">
                Call again
              </Button>
              {/* Demo CTA — probably the highest-intent audience on the
                  whole site. The visitor just experienced Scan2Call
                  seconds ago; pushing them to the interactive demo
                  (which then invites them to sign up with a ₦100 free
                  bonus) converts much better than a cold marketing
                  page. Emerald accent + Sparkles icon so it reads as a
                  reward, not just another link. */}
              <Link
                to="/demo"
                className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-sm bg-emerald-500 hover:bg-emerald-400 text-black text-xs font-body font-bold uppercase tracking-widest transition-colors"
                data-testid="vm-try-demo-cta"
              >
                <Sparkles className="w-3.5 h-3.5" /> Try Guard · Get ₦100 free
              </Link>
              <a
                href={GUARD_MARKETING_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-sm border border-emerald-500/40 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300 text-xs font-body font-bold uppercase tracking-widest transition-colors"
                data-testid="vm-get-guard-cta"
              >
                Get your own Guard <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>
        )}

        {stage === "ended" && !error && (
          <div className="bg-gray-900 border border-gray-800 rounded-sm p-5 text-center" data-testid="visitor-ended">
            <p className="font-heading text-xl font-black tracking-tighter mb-2">Call ended</p>
            <p className="text-xs text-gray-400 font-body mb-4">Thanks for using Guard Scan2Call.</p>
            <div className="flex flex-col gap-2">
              <Button onClick={callAgain} variant="outline" className="rounded-sm font-body" data-testid="ended-call-again">
                Call again
              </Button>
              <Link
                to="/demo"
                className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-sm bg-emerald-500 hover:bg-emerald-400 text-black text-xs font-body font-bold uppercase tracking-widest transition-colors"
                data-testid="ended-try-demo-cta"
              >
                <Sparkles className="w-3.5 h-3.5" /> Try Guard · Get ₦100 free
              </Link>
              <a
                href={GUARD_MARKETING_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-sm border border-emerald-500/40 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300 text-xs font-body font-bold uppercase tracking-widest transition-colors"
                data-testid="ended-get-guard-cta"
              >
                Get your own Guard <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
